// src/pages/ProjectEcommerceMobilier.jsx
import { useEffect, useRef, useState } from 'react';
import '../styles/ProjectPage.css';
import CaseStudyToc from '../components/CaseStudyToc';
import BackToTop from '../components/BackToTop';
import { projectsData } from '../data/projects';

// node-id de la page "Audit - Delivery Block" dans le fichier Figma, pour le
// deep-link depuis le texte du même nom en bas de page.
const AUDIT_NODE_ID = '54-1359';

const TOC_SECTIONS = [
    { id: 'contexte', label: 'Contexte' },
    {
        id: 'exercice-1',
        label: 'Box produit Vente privée',
        children: [
            { id: 'probleme-observe', label: 'Problème observé' },
            { id: 'approche', label: 'Approche' },
            { id: 'fonctionnalite-ajoutee', label: 'Fonctionnalité ajoutée' }
        ]
    },
    {
        id: 'exercice-2',
        label: 'Constat',
        children: [
            { id: 'observation', label: 'Observation' },
            { id: 'probleme', label: 'Problème' },
            { id: 'solution-proposee', label: 'Solution proposée' }
        ]
    }
];

export default function ProjectEcommerceMobilier() {
    const project = projectsData.find((p) => p.id === 'ecommerce-mobilier');
    const [embedSrc, setEmbedSrc] = useState(project.figmaEmbedSrc);
    const embedWrapperRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const goToAuditPage = () => {
        setEmbedSrc(`${project.figmaEmbedSrc}&node-id=${AUDIT_NODE_ID}`);
        embedWrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="project-page">
            <main className="project-main figma-project-main">
                <div className="figma-embed-wrapper" ref={embedWrapperRef}>
                    <iframe
                        className="figma-embed"
                        src={embedSrc}
                        title={`Maquette Figma – ${project.title}`}
                        allowFullScreen
                    />
                </div>

                <div className="project-content">
                    <h1>{project.title}</h1>
                    <p className="project-desc">{project.desc}</p>

                    {project.tech?.length > 0 && (
                        <div className="project-tech">
                            <h3>Compétences</h3>
                            <ul>
                                {project.tech.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="project-actions">
                        <a href={project.figmaUrl} target="_blank" rel="noopener noreferrer" className="back-btn">
                            Ouvrir dans Figma ↗
                        </a>
                    </div>
                </div>
            </main>

            <CaseStudyToc sections={TOC_SECTIONS} />

            <section className="case-study case-study--inline">
                <section className="case-study-section">
                    <h2 id="contexte">Contexte</h2>
                    <p>
                        Objectif : reprendre la box produit d'un site e-commerce de mobilier pour
                        l'intégrer proprement à son design system, et identifier une opportunité
                        d'amélioration ailleurs sur le site.
                    </p>
                </section>

                <section className="case-study-section">
                    <h2 id="exercice-1">Box produit Vente privée, revue et augmentée</h2>

                    <h3 id="probleme-observe">Problème observé</h3>
                    <p>
                        En analysant leur grille existante, j'ai identifié plusieurs incohérences : les
                        dimensions du produit sont noyées dans un titre à rallonge et disparaissent à la
                        troncature, et l'état épuisé repose sur un bandeau translucide dont le contraste
                        dépend de la photo.
                    </p>

                    <h3 id="approche">Approche</h3>
                    <p>
                        J'ai d'abord extrait les fondations visuelles directement depuis leur site :
                        typographie Poppins, palette de neutres, rayons, espacements, pour construire un
                        UI kit minimal cohérent avec leur identité. Chaque jeton est nommé par son usage
                        plutôt que par son apparence.
                    </p>
                    <ul className="decision-list">
                        <li>
                            <strong>Note et nombre d'avis toujours ensemble.</strong>
                            Une étoile seule n'est pas lisible par un lecteur d'écran et n'est pas
                            crédible sans compteur, donc les deux restent systématiquement liés.
                        </li>
                        <li>
                            <strong>Un badge opaque plutôt qu'un bandeau translucide.</strong>
                            L'état épuisé passe par un badge à fond plein, pour garantir la lisibilité
                            quelle que soit la photo produit derrière.
                        </li>
                    </ul>

                    <h3 id="fonctionnalite-ajoutee">Fonctionnalité ajoutée</h3>
                    <p>
                        En tant que cliente, j'ai identifié un besoin non couvert : voir le produit dans
                        sa couleur sans ouvrir la fiche. Au survol d'une pastille, la photo du produit
                        change directement sur la carte. Sur mobile, le tap remplace le survol. L'aperçu
                        du coloris se fait là où la décision commence : sur la liste.
                    </p>

                    <p>
                        <strong>Livrables : </strong>
                        UI kit complet · Composant avec 6 états (default, hover, focus, épuisé, sans
                        promo, sans avis) · Grille desktop avec vrais produits · Version mobile ·
                        Documentation accessibilité WCAG 2.1
                    </p>
                </section>

                <section className="case-study-section">
                    <h2 id="exercice-2">Constat</h2>

                    <h3 id="observation">Observation</h3>
                    <p>
                        En naviguant sur le site, j'ai remarqué que l'information de livraison est
                        fragmentée et incohérente selon les produits et les vendeurs. Le délai est
                        visible sur certaines fiches, mais les prix et les modes n'apparaissent qu'une
                        fois le produit ajouté au panier.
                    </p>

                    <h3 id="probleme">Problème</h3>
                    <p>
                        Selon le{' '}
                        <a
                            href="https://baymard.com/lists/cart-abandonment-rate"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Baymard Institute (2026) ↗
                        </a>
                        , 40 % des abandons panier sont causés par des frais inattendus découverts au
                        checkout. Sur ce site, un client peut voir 45,99 €
                        pour une livraison standard, et 92,99 € s'il souhaite que le meuble monte chez
                        lui, sans l'avoir anticipé.
                    </p>
                    <p>
                        Un concurrent direct, Maison du Monde, affiche le prix et le mode de livraison
                        directement sur la fiche produit, sans attendre le panier. Une bonne pratique
                        qu'ils n'appliquent cependant pas au délai, laissé de côté.
                    </p>
                    <p>
                        Ce qui rend le problème plus fort : leurs options premium (livraison dans la
                        pièce, avec déballage et reprise des emballages) sont un avantage concurrentiel
                        réel sur des acteurs comme IKEA, qui livre au trottoir. Cet avantage est invisible
                        jusqu'au dernier moment.
                    </p>

                    <h3 id="solution-proposee">Solution proposée</h3>
                    <p>
                        Un bloc livraison standardisé, posé juste sous le bouton « Ajouter au panier »,
                        décliné en trois variantes selon le produit : standard, premium, vendeur tiers.
                        Les modes et leurs prix sont visibles dès la fiche, dans le style sobre de leur
                        interface existante. Le détail est visible sur la page{' '}
                        <button type="button" className="inline-figma-link" onClick={goToAuditPage}>
                            « Audit - Delivery Block »
                        </button>{' '}
                        du fichier Figma.
                    </p>
                    <p>
                        Ce n'est pas une refonte, juste l'ajout ciblé d'une info là où elle manque.
                    </p>
                </section>
            </section>

            <BackToTop />
        </div>
    );
}
